import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BillingCalculation,
  BillingCustomer,
  BillingDestination,
  BillingService as ServiceType,
} from '@/types/billing';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Calculator,
  CheckCircle2,
  Copy,
  DollarSign,
  FileText,
  Info,
  Package,
  RotateCcw,
  Save,
  Scale,
  TrendingUp,
  Truck,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/useToast';
import BillingService from '@/services/billingService';

interface CalculationStep {
  title: string;
  description: string;
  amount: number;
  percentage?: number;
}

const SAVED_CALCULATIONS_STORAGE_KEY = 'billing-calculator-recent';

const loadSavedCalculations = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SAVED_CALCULATIONS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(entry => {
        const date = entry?.date ? new Date(entry.date) : new Date();
        return {
          id: String(entry?.id ?? Date.now()),
          type: entry?.type === 'purchase' ? 'purchase' : 'sales',
          weight: Number(entry?.weight) || 0,
          amount: Number(entry?.amount) || 0,
          date: Number.isNaN(date.getTime()) ? new Date() : date,
        };
      })
      .filter(entry => entry.weight > 0 || entry.amount > 0)
      .slice(0, 5);
  } catch (error) {
    console.warn('Unable to restore saved billing calculations.', error);
    return [];
  }
};

const BillingCalculator = () => {
  const { toast } = useToast();
  const [calculationType, setCalculationType] = useState<'sales' | 'purchase'>(
    'sales'
  );
  const [activeStep, setActiveStep] = useState(0);

  // Enhanced form data with validation
  const [formData, setFormData] = useState({
    awbId: '',
    serviceId: 'express',
    weight: '',
    shipmentValue: '',
    paymentMethod: 'CREDIT',
    customerId: '',
    vendorId: '',
    destination: '',
    originHub: '',
  });

  // Real-time calculation state
  const [calculation, setCalculation] = useState<BillingCalculation | null>(
    null
  );
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState<
    Array<{
      id: string;
      type: string;
      weight: number;
      amount: number;
      date: Date;
    }>
  >(loadSavedCalculations);

  // Master data from backend
  const [services, setServices] = useState<ServiceType[]>([]);
  const [customers, setCustomers] = useState<BillingCustomer[]>([]);
  const [destinations, setDestinations] = useState<BillingDestination[]>([]);

  // Load master data on component mount
  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (savedCalculations.length === 0) {
        localStorage.removeItem(SAVED_CALCULATIONS_STORAGE_KEY);
        return;
      }
      const payload = savedCalculations.map(entry => ({
        ...entry,
        date: entry.date.toISOString(),
      }));
      localStorage.setItem(
        SAVED_CALCULATIONS_STORAGE_KEY,
        JSON.stringify(payload)
      );
    } catch (error) {
      console.warn('Unable to persist saved billing calculations.', error);
    }
  }, [savedCalculations]);

  const loadMasterData = async () => {
    try {
      const [servicesData, customersData, destinationsData] = await Promise.all(
        [
          BillingService.getServices(),
          BillingService.getCustomers(),
          BillingService.getCountries(),
        ]
      );

      // Ensure we always set arrays, even if the service returns undefined
      setServices(Array.isArray(servicesData) ? servicesData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
      setDestinations(Array.isArray(destinationsData) ? destinationsData : []);
    } catch (error) {
      console.error('Error loading master data:', error);
      // Set empty arrays as fallback
      setServices([]);
      setCustomers([]);
      setDestinations([]);
      toast({
        title: 'Warning',
        description: 'Some master data could not be loaded. Using defaults.',
        variant: 'destructive',
      });
    }
  };

  // Real-time calculation engine using backend service
  const calculateBilling = useMemo(() => {
    if (!formData.weight || !formData.shipmentValue || !formData.serviceId) {
      return null;
    }

    const weight = parseFloat(formData.weight);
    const shipmentValue = parseFloat(formData.shipmentValue);

    if (weight <= 0 || shipmentValue <= 0) return null;

    // Try to get real calculation from backend service mock
    // This will use the mock calculation logic until backend routes are available
    const service =
      services.find(s => s._id === formData.serviceId) || services[0];
    const destination = destinations.find(d => d._id === formData.destination);

    if (!service) return null;

    // Base rate calculation
    const baseRate = weight * service.baseRate * (destination?.multiplier || 1);

    // Additional charges
    const fuelSurcharge = (baseRate * service.fuelSurcharge) / 100;
    const handlingCharge = weight * 5; // ₹5 per kg

    // COD charge (only for COD payments)
    const codCharge =
      formData.paymentMethod === 'COD' ? (shipmentValue * 2) / 100 : 0;

    // Insurance charge (0.5% of shipment value)
    const insuranceCharge = (shipmentValue * 0.5) / 100;

    const otherCharges = 0;
    const totalCharges =
      baseRate +
      fuelSurcharge +
      handlingCharge +
      codCharge +
      insuranceCharge +
      otherCharges;

    // GST calculation (18% for sales, varying for purchase)
    const gstRate = calculationType === 'sales' ? 18 : 12;
    const gstAmount = (totalCharges * gstRate) / 100;

    const finalAmount = totalCharges + gstAmount;

    return {
      baseRate: Math.round(baseRate * 100) / 100,
      fuelSurcharge: Math.round(fuelSurcharge * 100) / 100,
      handlingCharge: Math.round(handlingCharge * 100) / 100,
      codCharge: Math.round(codCharge * 100) / 100,
      insuranceCharge: Math.round(insuranceCharge * 100) / 100,
      otherCharges: Math.round(otherCharges * 100) / 100,
      totalCharges: Math.round(totalCharges * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      finalAmount: Math.round(finalAmount * 100) / 100,
      currency: 'INR',
    };
  }, [formData, calculationType, services, destinations]);

  // Update calculation when form data changes
  useEffect(() => {
    setCalculation(calculateBilling);
  }, [calculateBilling]);

  // Calculation steps for breakdown
  const calculationSteps: CalculationStep[] = useMemo(() => {
    if (!calculation) return [];

    const service = services.find(s => s._id === formData.serviceId);

    return [
      {
        title: 'Base Rate',
        description: `Weight (${formData.weight} kg) × Service rate`,
        amount: calculation.baseRate,
      },
      {
        title: 'Fuel Surcharge',
        description: 'Applied on base rate',
        amount: calculation.fuelSurcharge,
        percentage: service?.fuelSurcharge,
      },
      {
        title: 'Handling Charge',
        description: `₹5 per kg × ${formData.weight} kg`,
        amount: calculation.handlingCharge,
      },
      ...(calculation.codCharge > 0
        ? [
            {
              title: 'COD Charge',
              description: '2% of shipment value',
              amount: calculation.codCharge,
              percentage: 2,
            },
          ]
        : []),
      {
        title: 'Insurance',
        description: '0.5% of shipment value',
        amount: calculation.insuranceCharge,
        percentage: 0.5,
      },
      {
        title: 'GST',
        description: `${calculationType === 'sales' ? '18' : '12'}% on total charges`,
        amount: calculation.gstAmount,
        percentage: calculationType === 'sales' ? 18 : 12,
      },
    ];
  }, [calculation, formData, calculationType, services]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCalculate = async () => {
    if (!calculation) {
      toast({
        title: 'Incomplete Information',
        description: 'Please fill in all required fields to calculate billing',
        variant: 'destructive',
      });
      return;
    }

    setIsCalculating(true);

    try {
      // Try to get calculation from backend service
      const params = {
        awbId: formData.awbId || `temp_${Date.now()}`,
        serviceId: formData.serviceId,
        weight: parseFloat(formData.weight),
        shipmentValue: parseFloat(formData.shipmentValue),
        paymentMethod: formData.paymentMethod,
        customerId: formData.customerId,
        destination: formData.destination,
      };

      let response;
      if (calculationType === 'sales') {
        response = await BillingService.calculateSalesBilling(params);
      } else {
        response = await BillingService.calculatePurchaseBilling({
          ...params,
          vendorId: formData.vendorId,
        });
      }

      let finalAmount = calculation.finalAmount;
      if (response.success && response.data) {
        setCalculation(response.data);
        if (typeof response.data.finalAmount === 'number') {
          finalAmount = response.data.finalAmount;
        }
      }

      toast({
        title: 'Calculation Complete!',
        description: `Total amount: ₹${finalAmount.toLocaleString()}`,
      });
    } catch (error) {
      toast({
        title: 'Calculation Failed',
        description: 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const saveCalculation = () => {
    if (!calculation) return;

    const newCalculation = {
      id: Date.now().toString(),
      type: calculationType,
      weight: Number.parseFloat(formData.weight) || 0,
      amount: calculation.finalAmount,
      date: new Date(),
    };

    setSavedCalculations(prev => [newCalculation, ...prev.slice(0, 4)]);

    toast({
      title: 'Calculation Saved',
      description: 'Added to your recent calculations',
    });
  };

  const copyToClipboard = () => {
    if (!calculation) return;

    const service = services.find(s => s._id === formData.serviceId);
    const text = `
Billing Calculation (${calculationType.toUpperCase()})
Weight: ${formData.weight} kg
Service: ${service?.name || 'Unknown Service'}
Base Rate: ₹${calculation.baseRate.toLocaleString()}
Total Amount: ₹${calculation.finalAmount.toLocaleString()}
    `.trim();

    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to Clipboard',
      description: 'Calculation details copied successfully',
    });
  };

  const resetForm = () => {
    setFormData({
      awbId: '',
      serviceId: 'express',
      weight: '',
      shipmentValue: '',
      paymentMethod: 'CREDIT',
      customerId: '',
      vendorId: '',
      destination: '',
      originHub: '',
    });
    setCalculation(null);
    setActiveStep(0);
  };

  const clearSavedCalculations = () => {
    setSavedCalculations([]);
    toast({
      title: 'Recent Calculations Cleared',
      description: 'Your saved billing calculations have been removed.',
    });
  };

  const getCompletionPercentage = () => {
    const requiredFields: (keyof typeof formData)[] = [
      'weight',
      'shipmentValue',
      'serviceId',
    ];
    const filledFields = requiredFields.filter(field => formData[field]);
    return (filledFields.length / requiredFields.length) * 100;
  };

  const steps = [
    { title: 'Basic Info', fields: ['weight', 'shipmentValue'] as const },
    {
      title: 'Service & Destination',
      fields: ['serviceId', 'destination'] as const,
    },
    {
      title: 'Payment & Customer',
      fields: ['paymentMethod', 'customerId'] as const,
    },
  ];

  return (
    <div className='space-y-6'>
      {/* Enhanced Header */}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h3 className='text-2xl font-bold tracking-tight'>
            Billing Calculator
          </h3>
          <p className='text-muted-foreground'>
            Calculate shipping costs with real-time pricing and detailed
            breakdowns
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <Badge variant='outline' className='text-sm'>
            {getCompletionPercentage().toFixed(0)}% Complete
          </Badge>
          {calculation && (
            <Badge variant='default' className='text-sm'>
              ₹{calculation.finalAmount.toLocaleString()}
            </Badge>
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      <Card>
        <CardHeader>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <CardTitle className='flex items-center gap-2'>
              <Calculator className='h-5 w-5' />
              Calculation Progress
            </CardTitle>
            <Button variant='outline' size='sm' onClick={resetForm}>
              <RotateCcw className='h-4 w-4 mr-2' />
              Reset
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Progress value={getCompletionPercentage()} className='h-2' />
          <div className='flex justify-between mt-2 text-sm text-muted-foreground'>
            <span>Getting started</span>
            <span>Ready to calculate</span>
          </div>
        </CardContent>
      </Card>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Calculation Form */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Calculation Type */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <DollarSign className='h-5 w-5' />
                Calculation Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs
                value={calculationType}
                onValueChange={(value) =>
                  setCalculationType(value as 'sales' | 'purchase')
                }
              >
                <TabsList className='grid w-full grid-cols-2'>
                  <TabsTrigger
                    value='sales'
                    className='flex items-center gap-2'
                  >
                    <TrendingUp className='h-4 w-4' />
                    Sales Billing
                  </TabsTrigger>
                  <TabsTrigger
                    value='purchase'
                    className='flex items-center gap-2'
                  >
                    <Building2 className='h-4 w-4' />
                    Purchase Billing
                  </TabsTrigger>
                </TabsList>

                <TabsContent value='sales' className='mt-4'>
                  <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                    <div className='flex items-start gap-3'>
                      <Info className='h-5 w-5 text-blue-600 mt-0.5' />
                      <div>
                        <h4 className='font-medium text-blue-900'>
                          Sales Billing
                        </h4>
                        <p className='text-sm text-blue-700'>
                          Calculate charges for customer invoicing with 18% GST
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value='purchase' className='mt-4'>
                  <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
                    <div className='flex items-start gap-3'>
                      <Info className='h-5 w-5 text-green-600 mt-0.5' />
                      <div>
                        <h4 className='font-medium text-green-900'>
                          Purchase Billing
                        </h4>
                        <p className='text-sm text-green-700'>
                          Calculate vendor payments with 12% GST
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Step-by-step Form */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Package className='h-5 w-5' />
                Shipment Details
              </CardTitle>
              <CardDescription>
                Enter the shipment information for accurate billing calculation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs
                value={activeStep.toString()}
                onValueChange={value => setActiveStep(parseInt(value))}
              >
                <TabsList className='grid w-full grid-cols-3'>
                  {steps.map((step, index) => (
                    <TabsTrigger
                      key={index}
                      value={index.toString()}
                      className='flex items-center gap-2'
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          step.fields.every(field => formData[field])
                            ? 'bg-green-500'
                            : 'bg-gray-300'
                        }`}
                      />
                      {step.title}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value='0' className='space-y-4 mt-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label className='flex items-center gap-2'>
                        <Scale className='h-4 w-4' />
                        Weight (KG) *
                      </Label>
                      <Input
                        type='number'
                        step='0.1'
                        placeholder='Enter weight in kg'
                        value={formData.weight}
                        onChange={e =>
                          handleInputChange('weight', e.target.value)
                        }
                        className='text-lg'
                      />
                      {formData.weight && (
                        <p className='text-xs text-muted-foreground'>
                          Chargeable weight: {formData.weight} kg
                        </p>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label className='flex items-center gap-2'>
                        <DollarSign className='h-4 w-4' />
                        Shipment Value (₹) *
                      </Label>
                      <Input
                        type='number'
                        step='0.01'
                        placeholder='Enter shipment value'
                        value={formData.shipmentValue}
                        onChange={e =>
                          handleInputChange('shipmentValue', e.target.value)
                        }
                        className='text-lg'
                      />
                      {formData.shipmentValue && (
                        <p className='text-xs text-muted-foreground'>
                          For insurance and COD calculations
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='flex flex-col gap-2 sm:flex-row sm:justify-end'>
                    <Button
                      onClick={() => setActiveStep(1)}
                      disabled={!formData.weight || !formData.shipmentValue}
                    >
                      Next Step
                      <ArrowRight className='h-4 w-4 ml-2' />
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value='1' className='space-y-4 mt-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label className='flex items-center gap-2'>
                        <Truck className='h-4 w-4' />
                        Service Type *
                      </Label>
                      <Select
                        value={formData.serviceId}
                        onValueChange={value =>
                          handleInputChange('serviceId', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map(service => (
                            <SelectItem key={service._id} value={service._id}>
                              <div className='flex items-center justify-between w-full'>
                                <div>
                                  <div className='font-medium'>
                                    {service.name}
                                  </div>
                                  <div className='text-xs text-muted-foreground'>
                                    ₹{service.baseRate}/kg •{' '}
                                    {service.fuelSurcharge}% fuel surcharge
                                  </div>
                                </div>
                                <Badge variant='outline' className='ml-2'>
                                  {service.code}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label>Destination</Label>
                      <Select
                        value={formData.destination}
                        onValueChange={value =>
                          handleInputChange('destination', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select destination' />
                        </SelectTrigger>
                        <SelectContent>
                          {destinations.map(dest => (
                            <SelectItem key={dest._id} value={dest._id}>
                              <div className='flex items-center justify-between w-full'>
                                <span>{dest.name}</span>
                                <Badge variant='outline' className='ml-2'>
                                  {dest.zone}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className='flex flex-col gap-2 sm:flex-row sm:justify-between'>
                    <Button
                      variant='outline'
                      onClick={() => setActiveStep(0)}
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setActiveStep(2)}
                      disabled={!formData.serviceId}
                    >
                      Next Step
                      <ArrowRight className='h-4 w-4 ml-2' />
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value='2' className='space-y-4 mt-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label>Payment Method</Label>
                      <Select
                        value={formData.paymentMethod}
                        onValueChange={value =>
                          handleInputChange('paymentMethod', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='CASH'>Cash</SelectItem>
                          <SelectItem value='CHEQUE'>Cheque</SelectItem>
                          <SelectItem value='ONLINE'>Online</SelectItem>
                          <SelectItem value='CREDIT'>Credit</SelectItem>
                          <SelectItem value='PREPAID'>Prepaid</SelectItem>
                          <SelectItem value='COD'>Cash on Delivery</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.paymentMethod === 'COD' && (
                        <p className='text-xs text-yellow-600'>
                          COD charges will be applied (2% of shipment value)
                        </p>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label className='flex items-center gap-2'>
                        <Users className='h-4 w-4' />
                        {calculationType === 'sales' ? 'Customer' : 'Vendor'}
                      </Label>
                      <Select
                        value={
                          calculationType === 'sales'
                            ? formData.customerId
                            : formData.vendorId
                        }
                        onValueChange={value =>
                          handleInputChange(
                            calculationType === 'sales'
                              ? 'customerId'
                              : 'vendorId',
                            value
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={`Select ${calculationType === 'sales' ? 'customer' : 'vendor'}`}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map(customer => (
                            <SelectItem key={customer._id} value={customer._id}>
                              <div className='flex items-center justify-between w-full'>
                                <div>
                                  <div className='font-medium'>
                                    {customer.name}
                                  </div>
                                  <div className='text-xs text-muted-foreground'>
                                    {customer.code}
                                  </div>
                                </div>
                                <Badge variant='outline' className='ml-2'>
                                  {customer.tier}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className='flex flex-col gap-2 sm:flex-row sm:justify-between'>
                    <Button
                      variant='outline'
                      onClick={() => setActiveStep(1)}
                    >
                      Previous
                    </Button>
                    <Button onClick={handleCalculate} disabled={isCalculating}>
                      {isCalculating ? 'Calculating...' : 'Calculate Billing'}
                      <Calculator className='h-4 w-4 ml-2' />
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Results Panel */}
        <div className='space-y-6'>
          {/* Real-time Results */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Zap className='h-5 w-5' />
                Live Calculation
              </CardTitle>
              <CardDescription>
                Results update automatically as you enter data
              </CardDescription>
            </CardHeader>
            <CardContent>
              {calculation ? (
                <div className='space-y-4'>
                  <div className='text-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border'>
                    <div className='text-3xl font-bold text-blue-600'>
                      ₹{calculation.finalAmount.toLocaleString()}
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      Final Amount ({calculation.currency})
                    </div>
                  </div>

                  <Separator />

                  <div className='space-y-3'>
                    {calculationSteps.map((step, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between py-2'
                      >
                        <div className='flex-1'>
                          <div className='font-medium text-sm'>
                            {step.title}
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            {step.description}
                          </div>
                        </div>
                        <div className='text-right'>
                          <div className='font-medium'>
                            ₹{step.amount.toLocaleString()}
                          </div>
                          {step.percentage && (
                            <div className='text-xs text-muted-foreground'>
                              {step.percentage}%
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                    <Button
                      size='sm'
                      onClick={saveCalculation}
                      className='flex-1'
                    >
                      <Save className='h-4 w-4 mr-2' />
                      Save
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={copyToClipboard}
                      className='flex-1'
                    >
                      <Copy className='h-4 w-4 mr-2' />
                      Copy
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='text-center py-8 text-muted-foreground'>
                  <Calculator className='mx-auto h-12 w-12 opacity-20' />
                  <p className='mt-2'>
                    Enter shipment details to see live calculation
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Calculations */}
          {savedCalculations.length > 0 && (
            <Card>
              <CardHeader className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <CardTitle className='flex items-center gap-2'>
                  <FileText className='h-5 w-5' />
                  Recent Calculations
                </CardTitle>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={clearSavedCalculations}
                  className='self-start sm:self-auto'
                >
                  Clear History
                </Button>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {savedCalculations.map(calc => (
                    <div
                      key={calc.id}
                      className='flex items-center justify-between p-3 bg-muted rounded-lg'
                    >
                      <div>
                        <div className='font-medium'>
                          {calc.type.toUpperCase()}
                        </div>
                        <div className='text-sm text-muted-foreground'>
                          {calc.weight} kg • {calc.date.toLocaleDateString()}
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='font-bold'>
                          ₹{calc.amount.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips & Info */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Info className='h-5 w-5' />
                Calculation Tips
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex items-start gap-3 p-3 bg-blue-50 rounded-lg'>
                <CheckCircle2 className='h-5 w-5 text-blue-600 mt-0.5' />
                <div>
                  <div className='font-medium text-blue-900'>
                    Weight Accuracy
                  </div>
                  <div className='text-sm text-blue-700'>
                    Use the higher of actual or volumetric weight
                  </div>
                </div>
              </div>

              <div className='flex items-start gap-3 p-3 bg-orange-50 rounded-lg'>
                <AlertTriangle className='h-5 w-5 text-orange-600 mt-0.5' />
                <div>
                  <div className='font-medium text-orange-900'>COD Charges</div>
                  <div className='text-sm text-orange-700'>
                    Additional 2% applies for Cash on Delivery
                  </div>
                </div>
              </div>

              <div className='flex items-start gap-3 p-3 bg-green-50 rounded-lg'>
                <TrendingUp className='h-5 w-5 text-green-600 mt-0.5' />
                <div>
                  <div className='font-medium text-green-900'>
                    Fuel Surcharge
                  </div>
                  <div className='text-sm text-green-700'>
                    Varies by service type and current fuel prices
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BillingCalculator;
